import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FileSpreadsheet, Search, AlertTriangle, Loader2, Filter, Calendar } from 'lucide-react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as API from '../../../api';
import { PageTitle } from '../../../../_metronic/layout/core';
import { Modal } from 'react-bootstrap';
import ConfirmModal from '../../modals/ConfirmModal';
import DownloadModal from '../../modals/DownloadModal';
import { DataTable } from './DataTable';
import { StatsCard } from './StatsCard';
import { SearchInput } from './SearchInput';

const limit = 250;

function formatValue(raw: string, mask: 'cpf' | 'cnpj' | 'phone'): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  switch (mask) {
    case 'cpf': {
      let v = digits.slice(0, 11);
      v = v.replace(/^(\d{3})(\d)/, '$1.$2');
      v = v.replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2');
      v = v.replace(/^(\d{3}\.\d{3}\.\d{3})(\d)/, '$1-$2');
      return v;
    }
    case 'cnpj': {
      let c = digits.slice(0, 14);
      c = c.replace(/^(\d{2})(\d)/, '$1.$2');
      c = c.replace(/^(\d{2}\.\d{3})(\d)/, '$1.$2');
      c = c.replace(/^(\d{2}\.\d{3}\.\d{3})(\d)/, '$1/$2');
      c = c.replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{4})(\d)/, '$1-$2');
      return c;
    }
    case 'phone': {
      const p = digits.slice(0, 11);
      if (p.length <= 2) return `(${p}`;
      if (p.length <= 6) return `(${p.slice(0, 2)}) ${p.slice(2)}`;
      if (p.length <= 10) return `(${p.slice(0, 2)}) ${p.slice(2, 6)}-${p.slice(6)}`;
      return `(${p.slice(0, 2)}) ${p.slice(2, 7)}-${p.slice(7)}`;
    }
    default:
      return raw;
  }
}

interface ConectaReportProps {
  options?: { name: string; variable: string; type?: string; group?: string; defaultChecked?: boolean }[];
  inputs?: { label: string; variable: string; placeholder?: string; mask?: 'cpf' | 'cnpj' | 'phone' }[];
  showCalendar?: boolean;
  showDistributor?: boolean;
  showEnvironment?: boolean;
  showCNPJ?: boolean;
  showMySQL?: boolean;
  hideFilter?: boolean;
  title?: string;
  reportMethod?: string;
  warningMessage?: string;
}

const ConectaReport: React.FC<ConectaReportProps> = ({
  options = null,
  inputs = null,
  showCalendar = false,
  showDistributor = false,
  showEnvironment = false,
  showCNPJ = false,
  showMySQL = false,
  hideFilter = false,
  title = '',
  reportMethod,
  warningMessage
}) => {
  const [totalCount, setTotalCount] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedList, setSelectedList] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [allData, setAllData] = useState<any[]>([]);
  const [columns, setColumnns] = useState<any[]>([]);
  const [displayData, setDisplayData] = useState<any>({ columns: [], data: [] });
  const [exportPromise, setExportPromise] = useState<any>(null);
  const [downloadModal, setDownloadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searched, setSearched] = useState(false);
  const isMultiDistributor = reportMethod !== 'getConectaPedidos';
  const [environment, setEnvironment] = useState('UAT');
  const [selectedDistributors, setSelectedDistributors] = useState<any>(
    isMultiDistributor ? [] : null
  );
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [sellersFilter, setSellersFilter] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({});
  const [selectedRadioGroups, setSelectedRadioGroups] = useState<Record<string, string>>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [isDistributorDropdownOpen, setIsDistributorDropdownOpen] = useState(false);

  const disableExportButtons = !isMultiDistributor && !selectedDistributors;

  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
      '&:hover': {
        border: '1px solid #3B82F6',
      },
      minHeight: '38px',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#E0E7FF'
        : state.isFocused
        ? '#F3F4F6'
        : 'white',
      color: '#374151',
      cursor: 'pointer',
    }),
  };

  // Função para normalizar strings para comparação
  const normalizeString = (str: string): string => {
    return str.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim();
  };

  // Função para processar conteúdo da clipboard no formato específico
  const processClipboardContent = (content: string): any[] => {
    const pastedDistributors: any[] = [];
    
    // Divide o conteúdo por " - " para separar os distribuidores
    //const distributorNames = content.split(' - ').map(name => name.trim());
    const distributorNames = content.split('\n').map(name => name.trim()).filter(name => name.length > 0).concat(content.split(' - ').map(name => name.trim()));
    
    distributorNames.forEach(pastedName => {
      const normalizedPasted = normalizeString(pastedName);
      
      // Procura por correspondências mais precisas
      sellersFilter.forEach(distributor => {
        const normalizedDistributor = normalizeString(distributor.label);
        
        // Verifica correspondência exata
        if (normalizedPasted === normalizedDistributor) {
          pastedDistributors.push(distributor);
          return;
        }
        
        // Para casos específicos, verifica correspondência mais rigorosa
        const pastedParts = normalizedPasted.split(/\s+/);
        const distributorParts = normalizedDistributor.split(/\s+/);
        
        // Verifica se todas as partes do nome colado estão presentes no distribuidor
        // E se o número de partes é compatível (evita falsos positivos)
        const allPartsMatch = pastedParts.every(pastedPart => 
          distributorParts.some(distributorPart => 
            distributorPart === pastedPart || 
            (pastedPart.length > 2 && distributorPart.includes(pastedPart)) ||
            (distributorPart.length > 2 && pastedPart.includes(distributorPart))
          )
        );
        
        // Só aceita se todas as partes coincidem e tem pelo menos 2 partes significativas
        if (allPartsMatch && pastedParts.length >= 2 && distributorParts.length >= 2) {
          pastedDistributors.push(distributor);
        }
      });
    });
    
    // Remove duplicatas baseado no value
    const uniqueDistributors = pastedDistributors.filter((distributor, index, self) =>
      index === self.findIndex(d => d.value === distributor.value)
    );
    
    return uniqueDistributors;
  };

  // Listener para Ctrl+V
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'v' && isDistributorDropdownOpen) {
        e.preventDefault();
        
        try {
          const text = await navigator.clipboard.readText();
          const matchedDistributors = processClipboardContent(text);
          
          if (matchedDistributors.length > 0) {
            if (isMultiDistributor) {
              // Para multi-seleção, combina com os já selecionados
              const currentValues = selectedDistributors.map((d: any) => d.value);
              const newDistributors = matchedDistributors.filter((d:any) => !currentValues.includes(d.value));
              const updatedDistributors = [...selectedDistributors, ...newDistributors];
              setSelectedDistributors(updatedDistributors);
            } else {
              // Para seleção única, pega o primeiro encontrado
              setSelectedDistributors(matchedDistributors[0]);
            }
            
            // Feedback visual
            console.log(`${matchedDistributors.length} distribuidores selecionados automaticamente`);
          } else {
            console.log('Nenhum distribuidor encontrado no texto colado');
          }
        } catch (err) {
          console.error('Erro ao acessar clipboard:', err);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDistributorDropdownOpen, selectedDistributors, isMultiDistributor, sellersFilter]);

  const filterData = (data:any) => {
    if (reportMethod == 'getConectaLojistaVIP') {
      for (let a = 0; a < data.length; a++) {
        const lastColumnData = data[a][data[a].length - 1];
        if (lastColumnData.toLowerCase() == 'sim') data[a][data[a].length - 1] = 'yes';
        if (lastColumnData.toLowerCase() == 'nao') data[a][data[a].length - 1] = 'no';
        if (lastColumnData.toLowerCase() == 'não') data[a][data[a].length - 1] = 'no';
      }
    }
    return data;
  }

  const location = useLocation();

  const resetState = () => {
    setMessage('');
    setConfirmModal(false);
    setSelectedList(null);
    setIsLoading(false);
    setIsLoaded(false);
    setAllData([]);
    setColumnns([]);
    setDisplayData({ columns: [], data: [] });
    setExportPromise(null);
    setDownloadModal(false);
    setSearchTerm('');
    setEnvironment('UAT');
    setSelectedDistributors(isMultiDistributor ? [] : null);
    setSelectedDate(new Date());
    setSearched(false);
    setTotalCount(0);
    setSelectedOptions({});
    setSelectedRadioGroups({});
    setInputValues({});
  };

  useEffect(() => {
    resetState();
    if (
      !showCalendar &&
      !showDistributor &&
      !showEnvironment &&
      !(options && options.length > 0) &&
      !(inputs && inputs.length > 0)
    ) {
      getData();
    }
  }, [location.pathname]);

  useEffect(() => {
    setConfirmModal(message !== '');
  }, [message]);

  useEffect(() => {
    setDownloadModal(exportPromise != null);
  }, [exportPromise]);

  useEffect(() => {
    const filteredData = filterDataBySearch(allData, searchTerm);
    setDisplayData(filteredData.slice(0, limit));
  }, [searchTerm, allData]);

  useEffect(() => {
    if (showDistributor) {
      getSellersFilter();
    }
  }, [showDistributor]);

  useEffect(() => {
    if (options && Array.isArray(options)) {
      const initial: Record<string, boolean> = {};
      options.forEach(opt => {
        if (!opt.type || opt.type !== 'radio') {
          initial[opt.variable] = !!opt.defaultChecked;
        }
      });
      setSelectedOptions(initial);
    }
  }, [options]);

  useEffect(() => {
    if (options && Array.isArray(options)) {
      const initialRadio: Record<string, string> = {};
      options.forEach(opt => {
        if (opt.type === 'radio' && opt.defaultChecked && opt.group) {
          if (!initialRadio[opt.group]) {
            initialRadio[opt.group] = opt.variable;
          }
        }
      });
      setSelectedRadioGroups(initialRadio);
    }
  }, [options]);

  useEffect(() => {
    if (inputs && Array.isArray(inputs)) {
      const initialInputs: Record<string, string> = {};
      inputs.forEach(i => {
        initialInputs[i.variable] = '';
      });
      setInputValues(initialInputs);
    }
  }, [inputs]);

  

  const getData = async () => {
    setIsLoaded(false);
    setIsLoading(true);
    try {
      const response = await getMethodResponse();
      const formattedData = filterData(formatDates(response.data));
      setAllData(formattedData);
      setColumnns(response.data.columns);
      setDisplayData(formattedData.slice(0, limit));
      setTotalCount(response.count || 0);
      setIsLoaded(true);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setSearched(true);
      setIsLoading(false);
    }
  };

  const getParams = () => {
    const data: any = {
      distributors: isMultiDistributor
        ? selectedDistributors.map((i: any) => i.value)
        : selectedDistributors
        ? selectedDistributors.value
        : null,
      environment,
      selectedDate,
      reportMethod,
      title
    };
    if (options && Array.isArray(options)) {
      Object.keys(selectedOptions).forEach(key => {
        if (selectedOptions[key]) {
          data[key] = true;
        }
      });
      Object.keys(selectedRadioGroups).forEach(group => {
        if (selectedRadioGroups[group]) {
          data[group] = selectedRadioGroups[group];
        }
      });
    }
    if (inputs && Array.isArray(inputs)) {
      Object.keys(inputValues).forEach(key => {
        if (inputValues[key]) {
          data[key] = inputValues[key];
        }
      });
    }
    return data;
  };

  const getMethodResponse = async (
    exportReport: boolean = false,
    exportCSV: boolean = false,
    exportMySQL: boolean = false
  ) => {
    const data: any = getParams();
    if (exportReport) data['export'] = true;
    if (exportCSV) data['exportCSV'] = true;
    if (exportMySQL) data['mysql'] = true;
    if (reportMethod === 'getConectaSellers') {
      return API.getConectaSellers(data);
    } else if (reportMethod === 'getConectaSellerGroup') {
      return API.getConectaSellerGroup(data);
    } else if (reportMethod === 'getConectaSellerMapping') {
      return API.getConectaSellerMapping(data);
    } else if (reportMethod === 'getConectaBlockListSeller') {
      return API.getConectaBlockListSeller(data);
    } else if (reportMethod === 'getConectaPedidos') {
      data.tipoextracao = 'Completo';
      return API.getConectaPedidos(data);
    } else if (reportMethod === 'getConectaLojistaCustomer') {
      return API.getConectaLojistaCustomer(data);
    } else if (reportMethod === 'getConectaLojistaCompany') {
      return API.getConectaLojistaCompany(data);
    } else if (reportMethod === 'getConectaULPonderadas') {
      return API.getConectaULPonderadas(data);
    } else if (reportMethod === 'getConectaLojistaVIP') {
      return API.getConectaLojistaVIP(data);
    } else if (reportMethod === 'getConectaGrupoLojistaRoot') {
      return API.getConectaGrupoLojistaRoot(data);
    } else if (reportMethod === 'getConectaGrupoLojistaOpen') {
      return API.getConectaGrupoLojistaOpen(data);
    } else if (reportMethod === 'getConectaVendedor') {
      return API.getConectaVendedor(data);
    } else if (reportMethod === 'getConectaGrupoVendedor') {
      return API.getConectaGrupoVendedor(data);
    } else if (reportMethod === 'getConectaLimiteCredito') {
      return API.getConectaLimiteCredito(data);
    } else if (reportMethod === 'getConectaVoucherPrimario') {
      return API.getConectaVoucherPrimario(data);
    } else if (reportMethod === 'getConectaVoucherSecundario') {
      return API.getConectaVoucherSecundario(data);
    } else if (reportMethod === 'getConectaProduto') {
      return API.getConectaProduto(data);
    } else if (reportMethod === 'getConectaMarcas') {
      return API.getConectaMarcas(data);
    } else if (reportMethod === 'getConectaCategorias') {
      return API.getConectaCategorias(data);
    } else if (reportMethod === 'getConectaIndustrias') {
      return API.getConectaIndustrias(data);
    } else if (reportMethod === 'getConectaBlackListIndustria') {
      return API.getConectaBlackListIndustria(data);
    } else if (reportMethod === 'getDevITDistribuidores') {
      return API.getDevITDistribuidores(data);
    } else if (reportMethod === 'getDevITLojistas') {
      return API.getDevITLojistas(data);
    } else if (reportMethod === 'getDevITVendedores') {
      return API.getDevITVendedores(data);
    } else if (reportMethod === 'getFreshdeskLojistaCompany') {
      return API.getConectaFreshdeskLojistaCompany(data);
    } else if (reportMethod === 'getFreshdeskLojistaContact') {
      return API.getConectaFreshdeskLojistaContact(data);
    } else if (reportMethod === 'getFreshdeskVendedor') {
      return API.getConectaFreshdeskVendedor(data);
    } else if (reportMethod === 'getFreshdeskDistribuidor') {
      return API.getConectaFreshdeskDistribuidor(data);
    } else if (reportMethod === 'getConectaDiferencasSellers') {
      return API.getConectaDiferencasSellers(data);
    } else if (reportMethod === 'getConectaDiferencasSellerGroup') {
      return API.getConectaDiferencasSellerGroup(data);
    } else if (reportMethod === 'getConectaDiferencasBlockListSeller') {
      return API.getConectaDiferencasBlockListSeller(data);
    } else if (reportMethod === 'getConectaDiferencasPedidos') {
      data.tipoextracao = 'Diff';
      return API.getConectaDiferencasPedidos(data);
    } else if (reportMethod === 'getConectaDiferencasLojistaCustomer') {
      return API.getConectaDiferencasLojistaCustomer(data);
    } else if (reportMethod === 'getConectaDiferencasLojistaCompany') {
      return API.getConectaDiferencasLojistaCompany(data);
    } else if (reportMethod === 'getConectaDiferencasVendedores') {
      return API.getConectaDiferencasVendedores(data);
    } else if (reportMethod === 'getConectaDiferencasProduto') {
      return API.getConectaDiferencasProduto(data);
    } else if (reportMethod === 'getConectaDiferencasMarcas') {
      return API.getConectaDiferencasMarcas(data);
    } else if (reportMethod === 'getConectaDiferencasIndustrias') {
      return API.getConectaDiferencasIndustrias(data);
    } else if (reportMethod === 'getValidacaoGoLive') {
      return API.getValidacaoGoLive(data);
    } else if (reportMethod === 'getValidacaoEstoque') {
      return API.getValidacaoEstoque(data);
    } else if (reportMethod === 'getValidacaoEmail') {
      return API.getValidacaoEmail(data);
    } else if (reportMethod === 'getValidacaoTelefone') {
      return API.getValidacaoTelefone(data);
    } else if (reportMethod === 'getValidacaoSellerMapping') {
      return API.getValidacaoSellerMapping(data);
    } else if (reportMethod === 'getValidacaoCNPJMigrado') {
      return API.getValidacaoCNPJMigrado(data);
    } else if (reportMethod === 'conecta-extrato-lojista-customer-cnpj') {
      return API.getConectaLojistaCustomerCNPJ(data);
    } else if (reportMethod === 'conecta-extrato-lojista-company-cnpj') {
      return API.getConectaLojistaCompanyCNPJ(data);
    } else {
      return API.getConectaReportByType(data);
    }
  };

  const getSellersFilter = async () => {
    const response = await API.getConectaSellersFilter();
    const data = response.data.map((item: any) => ({
      label: item.name,
      value: item.IDDistribuidor,
    }));
    setSellersFilter(data);
  };

  const generateReport = async (exportType: string) => {
    const promise = getMethodResponse(true, exportType === 'csv', exportType === 'mysql');
    setExportPromise(promise);
  };

  const formatDates = (data: any) => {
    return data.data.map((row: any) => {
      for (let a = 0; a < row.length; a++) {
        if (isIsoDateString(row[a])) {
          row[a] = convertToBrazilianDate(row[a]);
        }
      }
      return row;
    });
  };

  const filterDataBySearch = (data: any[], term: string) => {
    if (!term) return data;
    const searchLower = term.toLowerCase();
    return data.filter((item: any) =>
      Object.values(item).some((value: any) =>
        value?.toString().toLowerCase().includes(searchLower)
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Modal show={confirmModal} onHide={() => setMessage('')} centered>
        <ConfirmModal title={selectedList?.NomeLista} message={message} onClose={() => setMessage('')} />
      </Modal>
      {downloadModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <DownloadModal promise={exportPromise} onClose={() => setExportPromise(null)} />
        </div>
      )}
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <PageTitle>{title}</PageTitle>
          {warningMessage && (
            <div className="mt-4 p-4 bg-red-200 border border-white-200 rounded-xl">
              <div className="flex items-center gap-3 text-black-800">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{warningMessage}</p>
              </div>
            </div>
          )}
          {searched && allData?.length > 0 && (
            <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <StatsCard
                title="Total de registros encontrados"
                value={totalCount}
                description="Total de registros na base de dados"
                icon={<FileSpreadsheet className="w-6 h-6" />}
              />
              <StatsCard
                title="Registros Carregados"
                value={allData.length}
                description={`Mostrando ${Math.min(limit, allData.length)} registros`}
                icon={<FileSpreadsheet className="w-6 h-6" />}
              />
              {searchTerm && (
                <StatsCard
                  title="Resultados da Busca"
                  value={filterDataBySearch(allData, searchTerm).length}
                  description="Registros encontrados com o termo pesquisado"
                  icon={<Search className="w-6 h-6" />}
                />
              )}
            </div>
          )}
        </div>
        {(showCalendar || showDistributor || showEnvironment || (options && options.length > 0) || (inputs && inputs.length > 0)) ? (
          <div className="bg-white rounded-xl shadow border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Filtros</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {showEnvironment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ambiente</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        value="UAT"
                        checked={environment === 'UAT'}
                        onChange={() => setEnvironment('UAT')}
                        className="form-radio text-blue-600"
                      />
                      <span className="text-gray-700">UAT</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        value="PRD"
                        checked={environment === 'PRD'}
                        onChange={() => setEnvironment('PRD')}
                        className="form-radio text-blue-600"
                      />
                      <span className="text-gray-700">PRD</span>
                    </label>
                  </div>
                </div>
              )}
              {showCalendar && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date: Date) => setSelectedDate(date)}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring focus:ring-blue-200"
                    />
                  </div>
                </div>
              )}
              {showDistributor && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Distribuidores</label>
                  <Select
                    isMulti={isMultiDistributor}
                    value={selectedDistributors}
                    onMenuOpen={() => setIsDistributorDropdownOpen(true)}
                    onMenuClose={() => setIsDistributorDropdownOpen(false)}
                    onChange={
                      isMultiDistributor
                        ? (newValue) => setSelectedDistributors(newValue ? [...newValue] : [])
                        : (newValue) => setSelectedDistributors(newValue)
                    }
                    options={sellersFilter}
                    placeholder={isDistributorDropdownOpen ? "Selecione os distribuidores (Ctrl+V para colar)" : "Selecione os distribuidores"}
                    styles={customSelectStyles}
                    className="text-sm"
                  />
                </div>
              )}
              {options && options.length > 0 && (
                <>
                  {options.filter(opt => opt.type === 'radio').length > 0 && (
                    <div className="col-span-1 md:col-span-2">
                      {Object.entries(
                        options
                          .filter(opt => opt.type === 'radio')
                          .reduce((groups, opt) => {
                            if (opt.group && !groups[opt.group]) groups[opt.group] = [];
                            if (opt.group) groups[opt.group].push(opt);
                            return groups;
                          }, {} as Record<string, any[]>)
                      ).map(([group, opts]) => (
                        <div key={group} className="mb-4">
                          <p className="block text-sm font-medium text-gray-700 mb-1">{group}</p>
                          {opts.map(opt => (
                            <label key={opt.variable} className="inline-flex items-center mr-4">
                              <input
                                type="radio"
                                name={group}
                                value={opt.variable}
                                checked={selectedRadioGroups[group] === opt.variable}
                                onChange={() =>
                                  setSelectedRadioGroups({
                                    ...selectedRadioGroups,
                                    [group]: opt.variable,
                                  })
                                }
                                className="form-radio"
                              />
                              <span className="ml-2 text-gray-700">{opt.name}</span>
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  {options.filter(opt => !opt.type || opt.type !== 'radio').map(opt => (
                    <div key={opt.variable}>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          checked={selectedOptions[opt.variable] || false}
                          onChange={(e) =>
                            setSelectedOptions({
                              ...selectedOptions,
                              [opt.variable]: e.target.checked,
                            })
                          }
                        />
                        <span className="ml-2 text-gray-700">{opt.name}</span>
                      </label>
                    </div>
                  ))}
                </>
              )}
              {inputs && inputs.length > 0 && inputs.map(input => {
                const raw = inputValues[input.variable] || '';
                const display = input.mask ? formatValue(raw, input.mask) : raw;
                return (
                  <div key={input.variable}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{input.label}</label>
                    <input
                      type="text"
                      value={display}
                      onChange={(e) => {
                        let rawValue: string;
                        if (input.mask) {
                          rawValue = e.target.value.replace(/\D/g, '');
                        } else {
                          rawValue = e.target.value;
                        }
                        setInputValues({ ...inputValues, [input.variable]: rawValue });
                      }}
                      placeholder={input.placeholder || ''}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring focus:ring-blue-200"
                    />
                  </div>
                );
              })}
            </div>
            {reportMethod == 'getConectaPedidos' || reportMethod == 'getConectaLojistaCustomer' || reportMethod == 'getConectaDiferencasPedidos' || reportMethod == 'getConectaLojistaCompany' ? (
              <div className="mb-6 flex flex-col sm:flex-row justify-end items-center gap-4">
                <button
                  onClick={() => generateReport('csv')}
                  disabled={disableExportButtons}
                  className={`w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm ${
                    disableExportButtons
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-blue-700 transition-colors duration-200 hover:shadow'
                  }`}
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar CSV
                </button>
                {reportMethod == 'getConectaLojistaCustomer' && (
                  <button
                    onClick={() => generateReport('planilha')}
                    disabled={disableExportButtons}
                    className={`w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm ${
                      disableExportButtons
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-blue-700 transition-colors duration-200 hover:shadow'
                    }`}
                  >
                    <FileSpreadsheet className="w-5 h-5" />
                    Exportar Planilha
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-6 flex justify-end gap-4">
                { !hideFilter && (<button
                  onClick={getData}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                >
                  <Filter className="w-5 h-5" />
                  Filtrar
                </button> )}
                {showMySQL && (
                  <button
                    onClick={() => generateReport('mysql')}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow"
                  >
                    <FileSpreadsheet className="w-5 h-5" />
                    Exportar Query
                  </button>
                )}
                <button
                  onClick={() => generateReport('csv')}
                  disabled={disableExportButtons}
                  className={`w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm ${
                    disableExportButtons
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-blue-700 transition-colors duration-200 hover:shadow'
                  }`}
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar CSV
                </button>
                <button
                  onClick={() => generateReport('planilha')}
                  disabled={disableExportButtons}
                  className={`w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm ${
                    disableExportButtons
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-blue-700 transition-colors duration-200 hover:shadow'
                  }`}
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar Planilha
                </button>
              </div>
            )}
          </div>
        ) : <div className="mt-6 flex justify-end gap-4 mb-4">
        
        {showMySQL && (
          <button
            onClick={() => generateReport('mysql')}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow"
          >
            <FileSpreadsheet className="w-5 h-5" />
            Exportar Query
          </button>
        )}
        <button
          onClick={() => generateReport('csv')}
          disabled={disableExportButtons}
          className={`w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm ${
            disableExportButtons
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-blue-700 transition-colors duration-200 hover:shadow'
          }`}
        >
          <FileSpreadsheet className="w-5 h-5" />
          Exportar CSV
        </button>
        <button
          onClick={() => generateReport('planilha')}
          disabled={disableExportButtons}
          className={`w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm ${
            disableExportButtons
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-blue-700 transition-colors duration-200 hover:shadow'
          }`}
        >
          <FileSpreadsheet className="w-5 h-5" />
          Exportar Planilha
        </button>
      </div>}
        {(isLoading || isLoaded) && (
          <>
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Buscar em todos os registros..."
              />
            </div>
            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <p className="mt-4 text-gray-500">Carregando dados...</p>
                </div>
              ) : !allData.length ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="bg-gray-100 rounded-full p-3">
                    <FileSpreadsheet className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="mt-4 text-gray-500">Nenhum registro encontrado</p>
                </div>
              ) : (
                <DataTable data={displayData} columns={columns} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const isIsoDateString = (value: any) => {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2}(?:\.\d{3}Z)?)?$/;
  return isoDateRegex.test(value);
};

const convertToBrazilianDate = (isoDate: string) => {
  if (typeof isoDate !== 'string') return null;
  const [datePart] =
    isoDate.split('T').length > 1 ? isoDate.split('T') : isoDate.split(' ');
  const [year, month, day] = datePart.split('-');
  return `${day}/${month}/${year}`;
};

export default ConectaReport;